import { Combobox } from '@ordo/ui/combobox'
import { Skeleton } from '@ordo/ui/skeleton'
import { useSenders } from 'hooks/api'
import { useMemo } from 'react'
import { Link } from 'react-router'
import type { SenderMethod } from 'types/senders'

import { senderOptions, smsSenderPresentation } from '../lib/sender-options'

export function SmsSenderSummary({ senderId }: { senderId: string }) {
  const { data: senders, loading } = useSenders('SMS')
  const selected = smsSenderPresentation(senders ?? [], senderId)
  if (loading) {
    return (
      <div className="space-y-1">
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-3 w-full" />
      </div>
    )
  }
  return (
    <div className="space-y-1 text-sm">
      <p>{selected.identity}</p>
      <p className="text-xs text-muted-foreground">{selected.notice}</p>
    </div>
  )
}

interface SenderSelectorProps {
  value: string
  onChange: (senderId: string) => void
  /**
   * Disable the picker (e.g. while saving). Doesn't affect the dropdown
   * options — only locks the trigger.
   */
  disabled?: boolean
  /**
   * Help text rendered below the picker. Defaults to a short explainer about
   * the singleton-from fallback. Pass `null` to hide.
   */
  helper?: React.ReactNode | null
  method?: SenderMethod
}

/**
 * Verified-only sender picker. Filters out unverified/locked SendGrid senders
 * because SendGrid will reject mail/send from them anyway. Empty value means
 * "use the configured default sender". SMS defaults are system-managed records.
 */
export function SenderSelector({ value, onChange, disabled, helper, method = 'EMAIL' }: SenderSelectorProps) {
  const { data: senders, loading } = useSenders(method)

  const options = useMemo(() => senderOptions(senders ?? [], method), [senders, method])
  const selected = smsSenderPresentation(senders ?? [], value)

  const hasUnverifiedOnly = !loading && (senders ?? []).length > 0 && options.length === 0

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium text-muted-foreground">Sender</span>
      <Combobox
        options={options}
        value={value}
        onValueChange={onChange}
        placeholder={
          method === 'SMS' ? (loading ? 'Default SMS sender' : selected.identity) : 'Use default from-address'
        }
        searchPlaceholder="Search senders..."
        emptyMessage={hasUnverifiedOnly ? 'No verified senders — verify one first' : 'No senders configured'}
        disabled={disabled}
      />
      {method === 'SMS' && !loading ? <p className="text-xs text-muted-foreground">{selected.notice}</p> : null}
      {helper === undefined ? (
        <p className="text-xs text-muted-foreground">
          Pick a verified {method === 'SMS' ? 'SMS number' : 'email sender'}. Leave empty to use the default sender.{' '}
          <Link to="/admin/senders" className="underline">
            Manage senders
          </Link>
        </p>
      ) : (
        helper
      )}
    </div>
  )
}
